import { readStrings } from "../content/authoring/selectorDecoders";
import { enumMembers, type ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import {
  JsonContractError,
  readArray,
  readBoolean,
  readInteger,
  readNullableInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../http/jsonDecoding";
import type { ThreadSettings } from "./liveMessages";
import { completeSettings } from "./threadsApi";

/**
 * Batch runs (FR-WF-5, `docs/components/workflows.md`, As built: batch runs): one Workflow
 * over the documents or segments of a source scope, one child Thread each, on the
 * starter's connector. Routes: `server/src/dmw/api/routes/threads/batches.py`.
 */

export type BatchState = ApiSchema<"BatchState">;
export type BatchItemState = ApiSchema<"BatchItemState">;
export type PauseReason = ApiSchema<"PauseReason">;
export type HumanStepHandling = ApiSchema<"HumanStepHandling">;
export type RouteCondition = ApiSchema<"RouteCondition">;
export type ChunkState = ApiSchema<"ChunkState">;
/** A source scope (V-1, `source_scope@1`), as the server's grammar has it. */
export type SourceScope = ApiSchema<"ScopePreviewRequestBody">["scope"];
export type BatchSettings = ApiSchema<"BatchSettings">;

export const BATCH_ITEM_STATES = enumMembers<BatchItemState>({
  pending: true,
  running: true,
  waiting_for_human: true,
  done: true,
  failed: true,
  cancelled: true,
});
const BATCH_STATES = enumMembers<BatchState>({
  planned: true,
  running: true,
  paused: true,
  completed: true,
  failed: true,
  cancelled: true,
});
const PAUSE_REASONS = enumMembers<PauseReason>({ requested: true, connector_offline: true, route_blocked: true, repeated_failures: true, item_withdrawn: true });
const ROUTE_CONDITIONS = enumMembers<RouteCondition>({ usable: true, rate_limited: true, blocked: true });
const CHUNK_STATES = enumMembers<ChunkState>({ proposing: true, proposed: true, failed: true, empty: true });
const HUMAN_STEP_HANDLINGS = enumMembers<HumanStepHandling>({ queue: true, skip: true });

export interface Usage {
  readonly turns: number;
  readonly inputTokens: number | null;
  readonly outputTokens: number | null;
  /** Currency → amount, for the calls that reported a cost. */
  readonly cost: Readonly<Record<string, number>>;
}

export interface BatchProblem {
  readonly code: string;
  readonly detail: string;
  readonly stepKey: string | null;
}

export interface BatchChunk {
  readonly number: number;
  readonly state: ChunkState;
  readonly itemCount: number;
  readonly changeSetId: string | null;
  readonly problem: BatchProblem | null;
}

export interface PlanUnit {
  readonly unit: string;
  readonly dependsOn: readonly string[];
}
export interface VitaAssembly {
  readonly membership: readonly (readonly string[])[];
  readonly vita: string;
  readonly complete: boolean;
  readonly missingUnits: readonly string[];
  readonly partOutputs: readonly string[];
  readonly digest: string;
}

export interface Batch {
  readonly planDigest: string | null;
  readonly units: readonly PlanUnit[];
  readonly unplannable: readonly { readonly vita: string; readonly reason: string }[];
  readonly assemblies: readonly VitaAssembly[];
  readonly batchId: string;
  readonly projectId: string;
  readonly title: string;
  readonly createdBy: string;
  readonly connectorId: string;
  readonly workflow: string;
  readonly corpusVersion: string;
  readonly unitCount: number;
  readonly state: BatchState;
  readonly pauseReason: PauseReason | null;
  readonly concurrencySetting: number | null;
  readonly humanSteps: HumanStepHandling;
  readonly itemsPerChangeSet: number | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly counts: Readonly<Record<BatchItemState, number>>;
  readonly usage: Usage;
  readonly averageItemSeconds: number | null;
  readonly estimatedRemainingSeconds: number | null;
  /** The limit in force: the batch's own, or its connector's capacity, capped. */
  readonly concurrency: number;
  readonly connectorOnline: boolean;
  readonly routeCondition: RouteCondition;
  readonly held: boolean;
  /** When the hold ends, if known (a provider's backoff, or the route's budget). */
  readonly heldUntil: string | null;
  readonly chunks: readonly BatchChunk[];
}

export interface BatchListing {
  readonly batchId: string;
  readonly title: string;
  readonly workflow: string;
  readonly state: BatchState;
  readonly pauseReason: PauseReason | null;
  readonly unitCount: number;
  readonly counts: Readonly<Record<BatchItemState, number>>;
  readonly createdAt: string;
}

export interface BatchStepTurn {
  readonly stepKey: string;
  readonly turnId: string;
  readonly outcome: string | null;
  /** The call's identity: batch, item, attempt and step. */
  readonly key: string;
}

export interface BatchItem {
  readonly index: number;
  readonly unit: string;
  readonly documentKey: string;
  readonly segmentPath: string | null;
  readonly state: BatchItemState;
  readonly attempt: number;
  readonly threadId: string | null;
  readonly problem: BatchProblem | null;
  readonly turns: readonly BatchStepTurn[];
  readonly usage: Usage;
  readonly earlierAttempts: readonly { readonly attempt: number; readonly threadId: string | null; readonly problem: BatchProblem | null }[];
  readonly chunk: number | null;
}

export interface ScopePreview {
  readonly unitCount: number;
  readonly units: readonly { readonly reference: string; readonly documentKey: string; readonly segmentPath: string | null }[];
}

export const BATCH_ITEMS_PAGE = 100;

function readNumberOrNull(object: JsonObject, member: string): number | null {
  const value = object[member];
  if (value === null) {
    return null;
  }
  if (typeof value !== "number") {
    throw new JsonContractError(`Expected "${member}" to be a number or null.`);
  }
  return value;
}

function decodeUsage(value: unknown): Usage {
  const usage = readObject(value, "usage");
  const cost = readObject(usage["cost"], "cost");
  return {
    turns: readInteger(usage, "turns"),
    inputTokens: readNullableInteger(usage, "input_tokens"),
    outputTokens: readNullableInteger(usage, "output_tokens"),
    cost: Object.fromEntries(Object.keys(cost).map((currency) => [currency, readNumberOrNull(cost, currency) ?? 0])),
  };
}

function decodeProblem(value: unknown): BatchProblem | null {
  if (value === null) {
    return null;
  }
  const problem = readObject(value, "problem");
  return { code: readString(problem, "code"), detail: readString(problem, "detail"), stepKey: readNullableString(problem, "step_key") };
}

function decodeCounts(value: unknown): Readonly<Record<BatchItemState, number>> {
  const counts = readObject(value, "counts");
  return Object.fromEntries(BATCH_ITEM_STATES.map((state) => [state, readInteger(counts, state)])) as Record<BatchItemState, number>;
}

function nullableOneOf<const T extends string>(object: JsonObject, member: string, allowed: readonly T[]): T | null {
  return object[member] === null ? null : readOneOf(object, member, allowed);
}

/** A `dmw-abox-batch/1` manifest: on a batch, or as the item an acceptance step decides (E4). */
export function decodeVitaAssembly(value: unknown): VitaAssembly {
  const assembly = readObject(value, "assembly");
  return { membership: readArray(assembly["membership"], "membership", (part) => readStrings(part, "statement parts")), vita: readString(assembly, "vita"), complete: readBoolean(assembly, "complete"), missingUnits: readStrings(assembly["missing_units"], "missing units"), partOutputs: readStrings(assembly["part_outputs"], "part outputs"), digest: readString(assembly, "digest") };
}

function decodeBatch(body: unknown): Batch {
  const batch = readObject(body, "batch");
  const settings = readObject(batch["settings"], "settings");
  const grouping = readObject(settings["change_sets"], "change_sets");
  return {
    planDigest: batch["plan_digest"] == null ? null : readString(batch, "plan_digest"),
    units: readArray(batch["units"] ?? [], "units", (value) => {
      const unit = readObject(value, "unit");
      return { unit: readString(unit, "unit"), dependsOn: readStrings(unit["depends_on"], "dependencies") };
    }),
    unplannable: readArray(batch["unplannable"] ?? [], "unplannable", (value) => {
      const vita = readObject(value, "vita");
      return { vita: readString(vita, "vita"), reason: readString(vita, "reason") };
    }),
    assemblies: readArray(batch["assemblies"] ?? [], "assemblies", decodeVitaAssembly),
    batchId: readString(batch, "batch_id"),
    projectId: readString(batch, "project_id"),
    title: readString(batch, "title"),
    createdBy: readString(batch, "created_by"),
    connectorId: readString(batch, "connector_id"),
    workflow: readString(batch, "workflow"),
    corpusVersion: readString(batch, "corpus_version"),
    unitCount: readInteger(batch, "unit_count"),
    state: readOneOf(batch, "state", BATCH_STATES),
    pauseReason: nullableOneOf(batch, "pause_reason", PAUSE_REASONS),
    concurrencySetting: readNullableInteger(settings, "concurrency"),
    humanSteps: readOneOf(settings, "human_steps", HUMAN_STEP_HANDLINGS),
    itemsPerChangeSet: grouping["mode"] === "chunked" ? readInteger(grouping, "items_per_change_set") : null,
    createdAt: readString(batch, "created_at"),
    startedAt: readNullableString(batch, "started_at"),
    finishedAt: readNullableString(batch, "finished_at"),
    counts: decodeCounts(batch["counts"]),
    usage: decodeUsage(batch["usage"]),
    averageItemSeconds: readNumberOrNull(batch, "average_item_seconds"),
    estimatedRemainingSeconds: readNumberOrNull(batch, "estimated_remaining_seconds"),
    concurrency: readInteger(batch, "concurrency"),
    connectorOnline: readBoolean(batch, "connector_online"),
    routeCondition: readOneOf(batch, "route_condition", ROUTE_CONDITIONS),
    held: readBoolean(batch, "held"),
    heldUntil: readNullableString(batch, "held_until"),
    chunks: readArray(batch["chunks"], "chunks", (value) => {
      const chunk = readObject(value, "chunk");
      return {
        number: readInteger(chunk, "number"),
        state: readOneOf(chunk, "state", CHUNK_STATES),
        itemCount: readInteger(chunk, "item_count"),
        changeSetId: readNullableString(chunk, "change_set_id"),
        problem: decodeProblem(chunk["problem"]),
      };
    }),
  };
}

function decodeListing(value: unknown): BatchListing {
  const batch = readObject(value, "batch listing");
  return {
    batchId: readString(batch, "batch_id"),
    title: readString(batch, "title"),
    workflow: readString(batch, "workflow"),
    state: readOneOf(batch, "state", BATCH_STATES),
    pauseReason: nullableOneOf(batch, "pause_reason", PAUSE_REASONS),
    unitCount: readInteger(batch, "unit_count"),
    counts: decodeCounts(batch["counts"]),
    createdAt: readString(batch, "created_at"),
  };
}

function decodeItem(value: unknown): BatchItem {
  const item = readObject(value, "batch item");
  return {
    index: readInteger(item, "index"),
    unit: readString(item, "unit"),
    documentKey: readString(item, "document_key"),
    segmentPath: readNullableString(item, "segment_path"),
    state: readOneOf(item, "state", BATCH_ITEM_STATES),
    attempt: readInteger(item, "attempt"),
    threadId: readNullableString(item, "thread_id"),
    problem: decodeProblem(item["problem"]),
    turns: readArray(item["turns"], "turns", (entry) => {
      const turn = readObject(entry, "turn");
      return { stepKey: readString(turn, "step_key"), turnId: readString(turn, "turn_id"), outcome: readNullableString(turn, "outcome"), key: readString(turn, "key") };
    }),
    usage: decodeUsage(item["usage"]),
    earlierAttempts: readArray(item["earlier_attempts"], "earlier_attempts", (entry) => {
      const attempt = readObject(entry, "attempt");
      return { attempt: readInteger(attempt, "attempt"), threadId: readNullableString(attempt, "thread_id"), problem: decodeProblem(attempt["problem"]) };
    }),
    chunk: readNullableInteger(item, "chunk"),
  };
}

function decodePreview(body: unknown): ScopePreview {
  const preview = readObject(body, "scope preview");
  return {
    unitCount: readInteger(preview, "unit_count"),
    units: readArray(preview["units"], "units", (value) => {
      const unit = readObject(value, "unit");
      return { reference: readString(unit, "reference"), documentKey: readString(unit, "document_key"), segmentPath: readNullableString(unit, "segment_path") };
    }),
  };
}

function batchPath(batchId: string, rest = ""): `/${string}` {
  return `/batches/${encodeURIComponent(batchId)}${rest}`;
}

function projectBatches(projectId: string, rest = ""): `/${string}` {
  return `/projects/${encodeURIComponent(projectId)}/batches${rest}`;
}

/** Which documents or segments a scope names at a corpus version (the first 50 of them). */
export function previewScope(projectId: string, corpusVersion: string, scope: SourceScope, signal?: AbortSignal): Promise<ScopePreview> {
  const body = { corpus_version: corpusVersion, scope } satisfies ApiSchema<"ScopePreviewRequestBody">;
  return requestJson({ method: "POST", path: projectBatches(projectId, "/scope-preview"), body: jsonBody(body), signal, decodeResponse: decodePreview });
}

export interface PlanBatchRequest {
  readonly projectId: string;
  readonly title: string;
  readonly workflow: string;
  readonly contentVersion: string;
  readonly corpusVersion: string;
  readonly scope: SourceScope;
  readonly settings: BatchSettings;
  readonly threadSettings: ThreadSettings;
  readonly connectorId: string;
  readonly clientRequestId: string;
}

/** A planned batch; idempotent by the client key. */
export function planBatch(request: PlanBatchRequest): Promise<Batch> {
  const body = {
    title: request.title,
    workflow: request.workflow,
    content_version: request.contentVersion,
    corpus_version: request.corpusVersion,
    scope: request.scope,
    settings: request.settings,
    thread_settings: completeSettings(request.threadSettings),
    connector_id: request.connectorId,
    client_request_id: request.clientRequestId,
  } satisfies ApiSchema<"PlanBatchBody">;
  return requestJson({ method: "POST", path: projectBatches(request.projectId), body: jsonBody(body), decodeResponse: decodeBatch });
}

export function readBatch(batchId: string, signal?: AbortSignal): Promise<Batch> {
  return requestJson({ method: "GET", path: batchPath(batchId), signal, decodeResponse: decodeBatch });
}

export function listBatches(projectId: string, signal?: AbortSignal): Promise<readonly BatchListing[]> {
  return requestJson({
    method: "GET",
    path: projectBatches(projectId),
    signal,
    decodeResponse: (body) => readArray(body, "batches", decodeListing),
  });
}

export function listBatchItems(batchId: string, fromIndex: number, state: BatchItemState | null, signal?: AbortSignal): Promise<readonly BatchItem[]> {
  return requestJson({
    method: "GET",
    path: withQuery(batchPath(batchId, "/items"), { from_index: fromIndex, limit: BATCH_ITEMS_PAGE, state }),
    signal,
    decodeResponse: (body) => readArray(body, "batch items", decodeItem),
  });
}

export type BatchControl = "start" | "pause" | "resume" | "cancel";

export function controlBatch(batchId: string, control: BatchControl): Promise<Batch> {
  return requestJson({ method: "POST", path: batchPath(batchId, `/${control}`), decodeResponse: decodeBatch });
}

export function retryBatchItem(batchId: string, index: number): Promise<BatchItem> {
  return requestJson({ method: "POST", path: batchPath(batchId, `/items/${index}/retry`), decodeResponse: decodeItem });
}

export function changeBatchSettings(batchId: string, concurrency: number | null, humanSteps: HumanStepHandling): Promise<Batch> {
  const body = { concurrency, human_steps: humanSteps } satisfies ApiSchema<"BatchSettingsChangeBody">;
  return requestJson({ method: "PATCH", path: batchPath(batchId, "/settings"), body: jsonBody(body), decodeResponse: decodeBatch });
}
